/**
 * use-me — 当前 admin 自助账户写 API（profile 域）
 *
 * 都是「本人的账户操作」（非 root-only）：
 *   - 改密码        PATCH  /api/v1/me/password   { current_password, new_password }
 *                   成功后服务端 bump session_version + 撤销全会话（含本会话）→ 前端须引导重新登录。
 *   - 登出单会话    DELETE /api/v1/me/sessions/:id
 *   - 撤销可信设备  DELETE /api/v1/me/trusted-devices/:id
 *   - 列会话        GET    /api/v1/me/sessions
 *   - 列可信设备    GET    /api/v1/me/trusted-devices
 *
 * 「登出所有其他设备/会话」复用 ~/hooks/use-auth 的 logout(true)（POST /auth/logout {all_devices:true}），
 * 不在此重复封装。
 */

import type {
  AdminUserResponse,
  NotificationPrefsResponse,
  RegenerateBackupCodesResponse,
  SelfSessionResponse,
  TotpBindChallenge,
  TrustedDeviceResponse,
  UpdateNotificationPrefsRequest,
} from '@novel-admin/shared';
import { apiRequest } from '~/api/client';

export const meApi = {
  /** PATCH /api/v1/me/profile — 改本人显示名（nickname）→ 返回更新后的账户投影 */
  updateProfile: (nickname: string) =>
    apiRequest<AdminUserResponse>({
      method: 'PATCH',
      path: '/api/v1/me/profile',
      body: { nickname },
    }),
  /** GET /api/v1/me/sessions — 本人有效登录会话（不含 token/hash） */
  listSessions: () =>
    apiRequest<SelfSessionResponse[]>({ method: 'GET', path: '/api/v1/me/sessions' }),
  /** DELETE /api/v1/me/sessions/:id — 撤销本人某会话（revoke_reason=user_revoke） */
  revokeSession: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'DELETE', path: `/api/v1/me/sessions/${id}` }),
  /** GET /api/v1/me/trusted-devices — 本人有效可信设备（不含 token/hash） */
  listTrustedDevices: () =>
    apiRequest<TrustedDeviceResponse[]>({ method: 'GET', path: '/api/v1/me/trusted-devices' }),
  /** DELETE /api/v1/me/trusted-devices/:id — 撤销本人某台可信设备 */
  revokeTrustedDevice: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'DELETE', path: `/api/v1/me/trusted-devices/${id}` }),
  /** PATCH /api/v1/me/password — 改密码（成功后全会话失效，须重新登录） */
  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest<{ ok: true }>({
      method: 'PATCH',
      path: '/api/v1/me/password',
      body: { current_password: currentPassword, new_password: newPassword },
    }),
  /** GET /api/v1/me/notification-preferences — 本人通知偏好 + 勿扰时段 */
  getNotificationPrefs: () =>
    apiRequest<NotificationPrefsResponse>({
      method: 'GET',
      path: '/api/v1/me/notification-preferences',
    }),
  /** PATCH /api/v1/me/notification-preferences — 改本人通知偏好（PATCH 语义，仅传字段覆盖） */
  updateNotificationPrefs: (input: UpdateNotificationPrefsRequest) =>
    apiRequest<NotificationPrefsResponse>({
      method: 'PATCH',
      path: '/api/v1/me/notification-preferences',
      body: input,
    }),
  /** POST /api/v1/me/2fa/backup-codes/regenerate — 重生成备用码（需 current_password；旧码全失效）→ 明文一次性 */
  regenerateBackupCodes: (currentPassword: string) =>
    apiRequest<RegenerateBackupCodesResponse>({
      method: 'POST',
      path: '/api/v1/me/2fa/backup-codes/regenerate',
      body: { current_password: currentPassword },
    }),
  /** POST /api/v1/me/2fa/totp/rebind/start — 验密码 → 返回新 TOTP secret challenge */
  startTotpRebind: (currentPassword: string) =>
    apiRequest<TotpBindChallenge>({
      method: 'POST',
      path: '/api/v1/me/2fa/totp/rebind/start',
      body: { current_password: currentPassword },
    }),
  /** POST /api/v1/me/2fa/totp/rebind/confirm — 对新 secret 出示 code → 轮转生效 */
  confirmTotpRebind: (code: string) =>
    apiRequest<{ ok: true }>({
      method: 'POST',
      path: '/api/v1/me/2fa/totp/rebind/confirm',
      body: { code },
    }),
};
